import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, Award, Users, TrendingUp } from 'lucide-react';

const LearnLanding: React.FC = () => {
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-teal/20 text-white py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              {/* Chamber Badge Placeholder */}
              <div className="mb-6 flex justify-center">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3 inline-flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-white/80">Endorsed by</p>
                    <p className="text-sm font-semibold">Marlborough Chamber of Commerce</p>
                  </div>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Practical AI for NZ SMEs — Learn it today, ship it this month
              </h1>
              
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Chamber-endorsed AI adoption program designed specifically for New Zealand small businesses. 
                Vendor-agnostic patterns, privacy-safe by design.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Link to="/learn/pricing">
                  <Button size="lg" variant="default" className="min-w-[200px]">
                    Enroll Now - $50 + GST
                  </Button>
                </Link>
                <Link to="/learn/catalog">
                  <Button size="lg" variant="outline" className="min-w-[200px] bg-white/10 hover:bg-white/20 text-white border-white/30">
                    View Course Catalog
                  </Button>
                </Link>
              </div>

              {/* Value Props */}
              <div className="grid md:grid-cols-4 gap-6 mt-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <CheckCircle className="w-8 h-8 text-brand-teal mb-2 mx-auto" />
                  <p className="text-sm font-medium">Vendor-agnostic patterns</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <CheckCircle className="w-8 h-8 text-brand-teal mb-2 mx-auto" />
                  <p className="text-sm font-medium">Privacy-safe by design</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <Award className="w-8 h-8 text-brand-teal mb-2 mx-auto" />
                  <p className="text-sm font-medium">Chamber-endorsed certificate</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <Users className="w-8 h-8 text-brand-teal mb-2 mx-auto" />
                  <p className="text-sm font-medium">Optional F2F workshops</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Learn */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-brand-navy">
                What You'll Learn
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-semibold mb-4 text-brand-navy">Foundations (Modules 1-5)</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">AI Basics for SMEs</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Responsible Use (NZ Privacy Act)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Prompt Hygiene & Output QA</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">From Notes to SOP</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Sector-Specific Lenses</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-semibold mb-4 text-brand-navy">Adoption (Modules 6-10)</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Use-Case Discovery & ROI</span>
                    </li>
                    <li className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Choosing & Governing Tools</span>
                    </li>
                    <li className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Data Hygiene</span>
                    </li>
                    <li className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Change Management</span>
                    </li>
                    <li className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Capstone Projects</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 bg-brand-teal/10 rounded-lg p-6 border border-brand-teal/20">
                <h3 className="text-xl font-semibold mb-4 text-brand-navy">Automation Patterns</h3>
                <p className="text-gray-700 mb-4">
                  Learn 6 proven automation patterns with variants for Microsoft, Google, Xero, and GoHighLevel:
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-brand-teal mr-2" />
                    <span className="text-sm text-gray-700">Email Triage Automation</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-brand-teal mr-2" />
                    <span className="text-sm text-gray-700">Lead Capture & CRM</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-brand-teal mr-2" />
                    <span className="text-sm text-gray-700">Meeting Notes → SOP</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-brand-teal mr-2" />
                    <span className="text-sm text-gray-700">Proposal Generation</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-brand-teal mr-2" />
                    <span className="text-sm text-gray-700">Ticket Triage</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-brand-teal mr-2" />
                    <span className="text-sm text-gray-700">Safety Checklist</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Notice */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-xl font-semibold mb-4 text-brand-navy">Privacy-Safe by Design</h3>
              <p className="text-gray-700 mb-4">
                We never require production client data. Submissions must be anonymised. 
                You can export or delete your data anytime.
              </p>
              <Link to="/learn/privacy" className="text-brand-teal hover:underline font-medium">
                Read our Privacy Policy →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-brand-navy text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business with AI?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join hundreds of NZ SMEs already using AI to save time and grow their business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/learn/pricing">
                <Button size="lg" variant="default" className="min-w-[200px]">
                  View Pricing
                </Button>
              </Link>
              <Link to="/learn/catalog">
                <Button size="lg" variant="outline" className="min-w-[200px] bg-white/10 hover:bg-white/20 text-white border-white/30">
                  Browse Catalog
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default LearnLanding;
